import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  CommandPalette,
  type CommandPaletteItem,
  ConfirmDialog,
  useHotkey,
} from '@rammetec/design-system'
import { useAvailableInstances, useAvailableModules, useOrgLabels, useSessionContext } from '@vectorhub/application'
import { resolveIcon } from '@/app/modules/Icon'

type Overlay = 'workspace' | 'moduleSearch' | 'orgSwitcher'

export function AppShortcuts() {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { currentOrgId, setCurrentOrgId, availableOrgIds } = useSessionContext()
  const instances = useAvailableInstances()
  const modules = useAvailableModules()

  const [overlay, setOverlay] = useState<Overlay | null>(null)

  const { moduleItems, moduleHrefs } = useMemo(() => {
    const modBySlug = new Map(modules.map((m) => [m.slug, m]))
    const items: CommandPaletteItem[] = []
    const hrefs = new Map<string, string>()
    for (const inst of instances) {
      const def = modBySlug.get(inst.slug)
      if (!def) continue
      const href = `/m/${inst.slug}`
      items.push({
        id: inst.id,
        label: inst.label ?? def.label,
        icon: resolveIcon(def.icon),
      })
      hrefs.set(inst.id, href)
    }
    return { moduleItems: items, moduleHrefs: hrefs }
  }, [instances, modules])

  const orgLabels = useOrgLabels(availableOrgIds)

  const orgItems = useMemo<CommandPaletteItem[]>(
    () => availableOrgIds.map((id) => {
      const org = orgLabels.get(id)
      return {
        id,
        label: org?.name ?? id,
        description: org?.abbreviation,
      }
    }),
    [availableOrgIds, orgLabels],
  )

  const activeSlug = /^\/m\/([^/?#]+)/.exec(pathname)?.[1]
  const activeId = instances.find((i) => i.slug === activeSlug)?.id

  const openWorkspace = useCallback(() => setOverlay('workspace'), [])
  const openModuleSearch = useCallback(() => setOverlay('moduleSearch'), [])
  const openOrgSwitcher = useCallback(() => setOverlay('orgSwitcher'), [])

  const overlayProps = useCallback(
    (which: Overlay) => ({
      open: overlay === which,
      onOpenChange: (open: boolean) => {
        if (!open) setOverlay((cur) => (cur === which ? null : cur))
      },
    }),
    [overlay],
  )

  useHotkey('Ctrl+Alt+D', openWorkspace)
  useHotkey('Ctrl+Alt+S', openModuleSearch)

  // Ctrl+Alt+E: on Brazilian ABNT keyboards, AltGr+E produces '€' so event.key
  // never matches 'e'. Bind by physical key code instead.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!e.ctrlKey || !e.altKey) return
      if (e.code === 'KeyE') { e.preventDefault(); openOrgSwitcher() }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [openOrgSwitcher])

  return (
    <>
      <ConfirmDialog
        {...overlayProps('workspace')}
        title="Ir para a área de trabalho?"
        description="O trabalho não salvo será perdido."
        confirmLabel="Ir"
        variant="warning"
        autoFocusConfirm
        onConfirm={() => {
          setOverlay(null)
          navigate('/')
        }}
      />

      <CommandPalette
        {...overlayProps('moduleSearch')}
        items={moduleItems}
        onSelect={(id) => {
          const href = moduleHrefs.get(id)
          if (href) navigate(href)
        }}
        activeId={activeId}
        searchPlaceholder="Buscar módulo…"
        warning="O trabalho não salvo será perdido."
      />

      <CommandPalette
        {...overlayProps('orgSwitcher')}
        items={orgItems}
        onSelect={(orgId) => {
          setCurrentOrgId(orgId)
          navigate('/')
        }}
        activeId={currentOrgId ?? undefined}
        searchPlaceholder="Buscar organização…"
        warning="Você será redirecionado para a área de trabalho."
      />
    </>
  )
}
